/**
 * Life4Earth V2 — Community Settings · Governor / Oversight section.
 *
 * DEV-1383 (epic DEV-1382). Lives in the "Oversight" tab of
 * CommunitySettingsModal. One place to (a) appoint a Governor over this
 * organization and (b) configure, per-governor, which of this org's
 * collections that governor can view.
 *
 * A Governor is a community ROLE SLOT (not a bespoke user grant). It
 * carries the tenant-neutral `governor_v1` permission set via the
 * standard "Governor" role_definition (migration 251). Creating the slot
 * with that definition's GUID makes it load-bearing automatically —
 * CommunityRoleSlotsController.ReconcileSlotRoleGrant issues the
 * slot-anchored community:view_descendants grant, and the occupant
 * inherits it (no extra backend call). The occupant is invited through
 * the existing slot-invitation flow, so the invitation surfaces in the
 * Action Center exactly like every other community role slot.
 *
 * Collection visibility is per-governor and configurable: a user-direct
 * `collection:view` grant (collection_viewer_v1 bundle) is issued to the
 * governor's user for each selected collection. Only collections OWNED BY
 * THIS ORGANIZATION are selectable (Skip, 2026-06-24).
 *
 * Composes with SPEC_GOVERN_PORTFOLIO_GRANTS.md and
 * POLICY_COMMUNITY_AUTHORITY.md (who may appoint — org administrators;
 * mirrored here by the modal's disabledAll prop).
 *
 * Per CLAUDE.md: all strings via useAppTranslation('community'); CSS
 * tokens only; logical properties; opaque overlay backgrounds;
 * case-insensitive GUID comparison.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import {
  fetchRoleSlots,
  upsertRoleSlot,
  deleteRoleSlot,
  type RoleSlotResponse,
} from "../../api/communityRoleSlots";
import {
  createCommunityRoleSlotInvitation,
  revokeCommunityRoleSlotAssignment,
} from "../../api/communityRoleSlotInvitations";
import {
  fetchAllCollections,
  type CollectionResponse,
} from "../../api/communityMetadata";
import {
  fetchAllSecurityUsers,
  type SecurityUserResponse,
} from "../../api/security";
import { permissionsApiV2 } from "../../security/v2/apiClient";
import type { PermissionGrantDto } from "../../security/v2/types";

/* ------------------------------------------------------------------ */
/*  Stable seed GUIDs (migration 251). md5-derived, never change.     */
/* ------------------------------------------------------------------ */

/** role_definitions row md5('platform:roledef:org_community_governor_v1') */
const GOVERNOR_DEFINITION_GUID = "24c5d56d-19ae-9717-58fb-c6769d530e30";
/** permission_sets row md5('platform:bundle:collection_viewer_v1') */
const COLLECTION_VIEWER_BUNDLE_GUID = "65b90f55-e1a9-6d7b-1c85-fecf307da1b2";

/* ------------------------------------------------------------------ */

interface Props {
  communityGuid: string;
  /** Mirrors the modal's disabledAll — read-only for non-admins. */
  disabledAll: boolean;
}

/** Case-insensitive GUID equality (CLAUDE.md security convention). */
function eqGuid(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  return a.toUpperCase() === b.toUpperCase();
}

export default function CommunityGovernorSection({ communityGuid, disabledAll }: Props) {
  const { t } = useAppTranslation("community");

  const [governorSlots, setGovernorSlots] = useState<RoleSlotResponse[]>([]);
  const [orgCollections, setOrgCollections] = useState<CollectionResponse[]>([]);
  const [users, setUsers] = useState<SecurityUserResponse[]>([]);
  /** collectionGuid (upper) -> active collection:view grants on it. */
  const [grantsByCollection, setGrantsByCollection] =
    useState<Record<string, PermissionGrantDto[]>>({});

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Add-governor form state.
  const [adding, setAdding] = useState(false);
  const [userQuery, setUserQuery] = useState("");
  const [selectedUserGuid, setSelectedUserGuid] = useState<string>("");
  const [selectedCollections, setSelectedCollections] = useState<Set<string>>(new Set());

  // ── Data loading ───────────────────────────────────────────

  const loadAll = useCallback(async () => {
    if (!communityGuid) return;
    setLoading(true);
    setError(null);
    try {
      const [slots, colls, allUsers] = await Promise.all([
        fetchRoleSlots(communityGuid),
        fetchAllCollections(communityGuid),
        fetchAllSecurityUsers(),
      ]);

      const govs = slots.filter((s) => eqGuid(s.sourceDefinitionGuid, GOVERNOR_DEFINITION_GUID));
      // Only collections OWNED BY THIS ORG are configurable / selectable.
      const owned = colls.filter((c) => c.isActive && eqGuid(c.communityGuid, communityGuid));

      // Fetch existing collection:view grants for each owned collection so
      // we can render which governors already see which collections.
      const grantPairs = await Promise.all(
        owned.map(async (c) => {
          try {
            const grants = await permissionsApiV2.listGrantsForScope("collection", c.collectionGuid);
            return [c.collectionGuid.toUpperCase(), grants] as const;
          } catch {
            return [c.collectionGuid.toUpperCase(), [] as PermissionGrantDto[]] as const;
          }
        }),
      );

      setGovernorSlots(govs);
      setOrgCollections(owned);
      setUsers(allUsers);
      setGrantsByCollection(Object.fromEntries(grantPairs));
    } catch (err: any) {
      setError(err?.message || t("governorSection.errorLoad"));
    } finally {
      setLoading(false);
    }
  }, [communityGuid, t]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  // ── Derived ────────────────────────────────────────────────

  const nameByGuid = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of users) m.set(u.userGuid.toUpperCase(), u.displayName || u.name || u.email);
    return m;
  }, [users]);

  const userMatches = useMemo(() => {
    const q = userQuery.trim().toLowerCase();
    if (!q) return [];
    return users
      .filter(
        (u) =>
          (u.displayName || "").toLowerCase().includes(q) ||
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q),
      )
      .slice(0, 8);
  }, [userQuery, users]);

  /** The collection:view grant a given user holds on a given collection, if any. */
  const grantFor = useCallback(
    (collectionGuid: string, userGuid: string | null): PermissionGrantDto | undefined => {
      if (!userGuid) return undefined;
      const grants = grantsByCollection[collectionGuid.toUpperCase()] ?? [];
      return grants.find(
        (g) =>
          eqGuid(g.userGuid, userGuid) &&
          eqGuid(g.permissionSetGuid, COLLECTION_VIEWER_BUNDLE_GUID) &&
          g.status !== "revoked" &&
          g.status !== "declined",
      );
    },
    [grantsByCollection],
  );

  // ── Actions ────────────────────────────────────────────────

  const handleAddGovernor = useCallback(async () => {
    if (!selectedUserGuid || busy) return;
    setBusy(true);
    setError(null);
    try {
      // 1. Create the load-bearing Governor slot (anchor grant auto-issued
      //    server-side from the Governor role_definition).
      const newSlotGuid =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const nextSort =
        governorSlots.reduce((max, s) => Math.max(max, s.sortOrder), 0) + 1 + 900;
      await upsertRoleSlot(newSlotGuid, communityGuid, {
        roleSlotGuid: newSlotGuid,
        displayTitle: "Governor",
        sortOrder: nextSort,
        // Oversight role, not a public-facing Key Community Leader.
        isHidden: true,
        sourceDefinitionGuid: GOVERNOR_DEFINITION_GUID,
      });

      // 2. Invite the user to occupy the slot (pending → Action Center).
      await createCommunityRoleSlotInvitation(newSlotGuid, { userGuid: selectedUserGuid });

      // 3. Issue collection:view grants for the selected org-owned collections.
      await Promise.all(
        Array.from(selectedCollections).map((collectionGuid) =>
          permissionsApiV2.createGrantForScope("collection", collectionGuid, {
            permissionSetGuid: COLLECTION_VIEWER_BUNDLE_GUID,
            userGuid: selectedUserGuid,
          }),
        ),
      );

      // Reset form + reload.
      setAdding(false);
      setUserQuery("");
      setSelectedUserGuid("");
      setSelectedCollections(new Set());
      await loadAll();
    } catch (err: any) {
      setError(err?.message || t("governorSection.errorAdd"));
    } finally {
      setBusy(false);
    }
  }, [selectedUserGuid, busy, governorSlots, communityGuid, selectedCollections, loadAll, t]);

  const handleRemoveGovernor = useCallback(
    async (slot: RoleSlotResponse) => {
      if (busy) return;
      setBusy(true);
      setError(null);
      try {
        const occupantGuid = slot.openAssignment?.userGuid ?? slot.linkedUserGuid ?? null;

        // Revoke the slot assignment (if any) — surfaces a notice to the user.
        if (slot.openAssignment?.assignmentGuid) {
          await revokeCommunityRoleSlotAssignment(slot.openAssignment.assignmentGuid, {
            reason: "Governor role removed",
          });
        }

        // Revoke this occupant's collection:view grants on org-owned collections.
        if (occupantGuid) {
          const toRevoke: string[] = [];
          for (const c of orgCollections) {
            const g = grantFor(c.collectionGuid, occupantGuid);
            if (g) toRevoke.push(g.grantGuid);
          }
          await Promise.all(toRevoke.map((grantGuid) => permissionsApiV2.revokeGrant(grantGuid)));
        }

        // Soft-delete the slot itself (revokes the slot-anchored grant).
        await deleteRoleSlot(slot.roleSlotGuid);

        await loadAll();
      } catch (err: any) {
        setError(err?.message || t("governorSection.errorRemove"));
      } finally {
        setBusy(false);
      }
    },
    [busy, orgCollections, grantFor, loadAll, t],
  );

  /** Toggle a single collection's visibility for an existing governor. */
  const handleToggleCollection = useCallback(
    async (slot: RoleSlotResponse, collection: CollectionResponse, nextOn: boolean) => {
      const occupantGuid = slot.openAssignment?.userGuid ?? slot.linkedUserGuid ?? null;
      if (!occupantGuid || busy) return;
      setBusy(true);
      setError(null);
      try {
        const existing = grantFor(collection.collectionGuid, occupantGuid);
        if (nextOn && !existing) {
          await permissionsApiV2.createGrantForScope("collection", collection.collectionGuid, {
            permissionSetGuid: COLLECTION_VIEWER_BUNDLE_GUID,
            userGuid: occupantGuid,
          });
        } else if (!nextOn && existing) {
          await permissionsApiV2.revokeGrant(existing.grantGuid);
        }
        await loadAll();
      } catch (err: any) {
        setError(err?.message || t("governorSection.errorToggleCollection"));
      } finally {
        setBusy(false);
      }
    },
    [busy, grantFor, loadAll, t],
  );

  // ── Render ─────────────────────────────────────────────────

  return (
    <div style={{ marginBlockEnd: 18 }}>
      <div style={titleStyle}>{t("governorSection.title")}</div>
      <div style={subtitleStyle}>{t("governorSection.subtitle")}</div>

      {error && (
        <div style={errorBoxStyle} role="alert">
          {error}
        </div>
      )}

      {loading && governorSlots.length === 0 && (
        <div style={mutedItalicStyle}>{t("governorSection.loading")}</div>
      )}

      {/* Existing governors */}
      {governorSlots.map((slot) => {
        const occupantGuid = slot.openAssignment?.userGuid ?? slot.linkedUserGuid ?? null;
        const occupantName =
          slot.openAssignment?.userDisplayName ||
          (occupantGuid ? nameByGuid.get(occupantGuid.toUpperCase()) : null) ||
          t("governorSection.vacant");
        const pending = slot.openAssignment?.status === "pending_invitation";

        return (
          <div key={slot.roleSlotGuid} style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)" }}>
                  {occupantName}
                  {pending && (
                    <span style={pendingPillStyle}>{t("governorSection.pendingInvite")}</span>
                  )}
                </div>
                <div style={{ fontSize: 11, color: "var(--text-muted)", marginBlockStart: 2 }}>
                  {t("governorSection.roleLabel")}
                </div>
              </div>
              {!disabledAll && (
                <button
                  type="button"
                  onClick={() => void handleRemoveGovernor(slot)}
                  disabled={busy}
                  style={btnDangerStyle}
                >
                  {t("governorSection.actionRemove")}
                </button>
              )}
            </div>

            {/* Per-governor collection visibility */}
            <div style={{ marginBlockStart: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-secondary)", marginBlockEnd: 6 }}>
                {t("governorSection.collectionsLabel")}
              </div>
              {orgCollections.length === 0 && (
                <div style={mutedItalicStyle}>{t("governorSection.noOrgCollections")}</div>
              )}
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {orgCollections.map((c) => {
                  const on = !!grantFor(c.collectionGuid, occupantGuid);
                  return (
                    <label key={c.collectionGuid} style={checkboxRowStyle}>
                      <input
                        type="checkbox"
                        checked={on}
                        disabled={disabledAll || busy || !occupantGuid}
                        onChange={(e) => void handleToggleCollection(slot, c, e.target.checked)}
                      />
                      <span style={{ fontSize: 13, color: "var(--text-primary)" }}>
                        {c.collectionName}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })}

      {!loading && governorSlots.length === 0 && !adding && (
        <div style={mutedItalicStyle}>{t("governorSection.empty")}</div>
      )}

      {/* Add-governor form */}
      {adding && !disabledAll && (
        <div style={cardStyle}>
          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-secondary)", marginBlockEnd: 6 }}>
            {t("governorSection.addUserLabel")}
          </div>
          {selectedUserGuid ? (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBlockEnd: 10 }}>
              <span style={{ fontSize: 13, color: "var(--text-primary)" }}>
                {nameByGuid.get(selectedUserGuid.toUpperCase())}
              </span>
              <button
                type="button"
                onClick={() => {
                  setSelectedUserGuid("");
                  setUserQuery("");
                }}
                style={btnSecondaryStyle}
              >
                {t("governorSection.changeUser")}
              </button>
            </div>
          ) : (
            <div style={{ position: "relative", marginBlockEnd: 10 }}>
              <input
                type="text"
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder={t("governorSection.userSearchPlaceholder")}
                style={inputStyle}
              />
              {userMatches.length > 0 && (
                <div style={suggestStyle}>
                  {userMatches.map((u) => (
                    <button
                      key={u.userGuid}
                      type="button"
                      onClick={() => {
                        setSelectedUserGuid(u.userGuid);
                        setUserQuery(u.displayName || u.name || u.email);
                      }}
                      style={suggestItemStyle}
                    >
                      <span style={{ fontSize: 13, color: "var(--text-primary)" }}>
                        {u.displayName || u.name}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{u.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ fontSize: 12, fontWeight: 500, color: "var(--text-secondary)", marginBlockEnd: 6 }}>
            {t("governorSection.collectionsLabel")}
          </div>
          {orgCollections.length === 0 && (
            <div style={mutedItalicStyle}>{t("governorSection.noOrgCollections")}</div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBlockEnd: 12 }}>
            {orgCollections.map((c) => (
              <label key={c.collectionGuid} style={checkboxRowStyle}>
                <input
                  type="checkbox"
                  checked={selectedCollections.has(c.collectionGuid)}
                  onChange={(e) => {
                    setSelectedCollections((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) next.add(c.collectionGuid);
                      else next.delete(c.collectionGuid);
                      return next;
                    });
                  }}
                />
                <span style={{ fontSize: 13, color: "var(--text-primary)" }}>{c.collectionName}</span>
              </label>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button
              type="button"
              onClick={() => {
                setAdding(false);
                setUserQuery("");
                setSelectedUserGuid("");
                setSelectedCollections(new Set());
              }}
              disabled={busy}
              style={btnSecondaryStyle}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              onClick={() => void handleAddGovernor()}
              disabled={!selectedUserGuid || busy}
              style={btnPrimaryStyle}
            >
              {busy ? t("governorSection.saving") : t("governorSection.actionAppoint")}
            </button>
          </div>
        </div>
      )}

      {!adding && !disabledAll && (
        <button
          type="button"
          onClick={() => setAdding(true)}
          disabled={busy}
          style={{ ...btnPrimaryStyle, marginBlockStart: 4 }}
        >
          {t("governorSection.actionAddGovernor")}
        </button>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Inline styles — CSS tokens + logical properties only.             */
/* ------------------------------------------------------------------ */

const titleStyle: React.CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: "var(--text-primary)",
  marginBlockEnd: 6,
};

const subtitleStyle: React.CSSProperties = {
  fontSize: 12,
  color: "var(--text-secondary)",
  marginBlockEnd: 10,
  lineHeight: 1.5,
};

const mutedItalicStyle: React.CSSProperties = {
  fontSize: 13,
  color: "var(--text-muted)",
  fontStyle: "italic",
};

const errorBoxStyle: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--color-error-bg, rgba(220, 60, 60, 0.1))",
  color: "var(--color-error)",
  border: "1px solid var(--color-error)",
  borderRadius: 4,
  fontSize: 13,
  marginBlockEnd: 10,
};

const cardStyle: React.CSSProperties = {
  padding: "10px 14px",
  background: "var(--bg-elevated)",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  marginBlockEnd: 10,
};

const checkboxRowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  cursor: "pointer",
};

const pendingPillStyle: React.CSSProperties = {
  marginInlineStart: 8,
  padding: "1px 8px",
  fontSize: 10,
  borderRadius: 999,
  background: "var(--color-warning-bg, rgba(200,160,40,0.15))",
  color: "var(--color-warning)",
  verticalAlign: "middle",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "6px 10px",
  background: "var(--bg-surface)",
  color: "var(--text-primary)",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 13,
};

// Suggestion dropdown floats over content → MUST be opaque (CLAUDE.md).
const suggestStyle: React.CSSProperties = {
  position: "absolute",
  insetInlineStart: 0,
  insetInlineEnd: 0,
  marginBlockStart: 2,
  background: "var(--bg-tertiary)",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  boxShadow: "0 8px 24px rgba(0,0,0,0.35)",
  zIndex: 20,
  maxHeight: 220,
  overflowY: "auto",
};

const suggestItemStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 2,
  width: "100%",
  padding: "6px 10px",
  background: "transparent",
  border: "none",
  borderBlockEnd: "1px solid var(--border-default)",
  cursor: "pointer",
  textAlign: "start",
};

const btnPrimaryStyle: React.CSSProperties = {
  padding: "6px 14px",
  background: "var(--accent-primary)",
  color: "#ffffff",
  border: "none",
  borderRadius: 4,
  fontSize: 13,
  cursor: "pointer",
};

const btnSecondaryStyle: React.CSSProperties = {
  padding: "6px 14px",
  background: "transparent",
  color: "var(--text-primary)",
  border: "1px solid var(--border-default)",
  borderRadius: 4,
  fontSize: 13,
  cursor: "pointer",
};

const btnDangerStyle: React.CSSProperties = {
  padding: "6px 14px",
  background: "transparent",
  color: "var(--color-error)",
  border: "1px solid var(--color-error)",
  borderRadius: 4,
  fontSize: 13,
  cursor: "pointer",
};
